import type { Transaction, DocumentReference } from 'firebase/firestore';

/**
 * Gold balance primitives — the single definition of how a balance is read and
 * written on a user document.
 *
 * Background: gold lived in three separate spend implementations and four
 * different read conventions (`goldBalance ?? gold`, `goldBalance || gold`,
 * `gold || 0`, and inline arithmetic), each duplicating the "write both fields"
 * rule. This module exists so there is exactly one place that knows the shape of
 * a balance, which is also the seam the server-side migration will move behind:
 * when gold mutation becomes Cloud-Function-owned, these helpers are what the
 * callables use, and the client keeps only `readGoldBalance` for display.
 *
 * The dual `gold` / `goldBalance` fields are legacy. They are currently in
 * perfect sync in production (0 of 962 user documents drift), so they are kept
 * mirrored rather than collapsed — collapsing is read-site churn that does not
 * belong on the migration's critical path.
 */

/**
 * Shape of the balance fields as they exist on a user document.
 *
 * A type alias rather than an interface on purpose: Firestore's update signature
 * expects an index-signature-compatible object, and interfaces do not get an
 * implicit index signature, so an interface here fails to type-check at
 * `updateDoc` / `transaction.update` call sites.
 */
export type GoldFields = {
  gold: number;
  goldBalance: number;
};

/**
 * The canonical balance for a user document.
 *
 * Uses `??` rather than `||`, deliberately. With `||`, a user whose
 * `goldBalance` is genuinely 0 falls through to a stale non-zero `gold` and can
 * spend money they do not have. `??` only falls through when the field is
 * absent, so it is never more permissive than the convention it replaces — it
 * can refuse a spend the old code would have allowed, never the reverse.
 */
export function readGoldBalance(userData: any): number {
  const raw = userData?.goldBalance ?? userData?.gold ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The fields to write for a new balance. Always returns BOTH keys so the legacy
 * mirror can never drift because one call site forgot to update it.
 */
export function goldFields(newBalance: number): GoldFields {
  const n = Math.max(0, Math.floor(Number(newBalance) || 0));
  return { gold: n, goldBalance: n };
}

/** Thrown when a spend exceeds the available balance. */
export const INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS';

/**
 * Apply a spend to a user document from INSIDE an existing transaction.
 *
 * This exists for callers that must charge gold atomically with something else —
 * creating a forum or tribe, where deducting separately would leave the user
 * charged if the creation then failed. Those callers keep their own transaction
 * and use this for the balance arithmetic only.
 *
 * Callers doing a standalone spend should use `EconomyService.deductGold`
 * instead, which additionally writes the audit entry.
 *
 * @returns the new balance
 * @throws  Error(INSUFFICIENT_FUNDS) when the balance is too low
 */
export function applyGoldSpend(
  transaction: Transaction,
  userRef: DocumentReference,
  userData: any,
  amount: number,
  /** Overrides the thrown message. Callers with their own user-facing copy pass
   *  it here so consolidating the arithmetic does not change what users read. */
  insufficientMessage?: string,
): number {
  const current = readGoldBalance(userData);
  const cost = Math.max(0, Math.floor(Number(amount) || 0));
  if (current < cost) {
    throw new Error(insufficientMessage || `${INSUFFICIENT_FUNDS}: Need ${cost}, have ${current}`);
  }
  const newBalance = current - cost;
  transaction.update(userRef, goldFields(newBalance));
  return newBalance;
}

/**
 * Apply a credit to a user document from inside an existing transaction.
 * Mirrors applyGoldSpend for the earn paths (daily streak, ad reward).
 *
 * @returns the new balance
 */
export function applyGoldCredit(
  transaction: Transaction,
  userRef: DocumentReference,
  userData: any,
  amount: number,
  extraFields: Record<string, any> = {},
): number {
  const current = readGoldBalance(userData);
  const credit = Math.max(0, Math.floor(Number(amount) || 0));
  const newBalance = current + credit;
  transaction.update(userRef, { ...goldFields(newBalance), ...extraFields });
  return newBalance;
}
